import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  useParams,
  useSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "../../styles/Product/list.css";
import { useTranslation } from "react-i18next";
import ProductCard from "./card";
import BaseApi from "../../services/base-api";
import { Product } from "../../types/Product";
import { Filter, Package } from "lucide-react";
import { useCategories } from "../../context/CategoriesContext";
// import Pagination from "../Pagination";
import { usePagination } from "../../hooks/usePagination";
import { useGiftData } from "./gift-integration";
import LoadingComponent from "../LoadingComponent";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { useToast } from "../../context/ToastContext";
import Price from "../Currency/Price";

const PRODUCTS_PER_PAGE = 12; // Adjust this number as needed

const ProductList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { success, err } = useToast();
  const { permissions, isAuthenticated } = useAuth();

  // Check if user has permission to create or edit products
  const canCreateProduct = hasPermission(
    permissions,
    PERMISSIONS.CREATE_PRODUCTS,
  );
  const canEditProduct = hasPermission(permissions, PERMISSIONS.EDIT_PRODUCTS);
  // const canDelete = hasPermission(permissions, PERMISSIONS.DELETE_PRODUCTS);
  // const canView = hasPermission(permissions, PERMISSIONS.VIEW_PRODUCTS);
  // User can add products if they have either create or edit permission
  const canCreateAddProduct = canCreateProduct || canEditProduct;

  const { categories } = useCategories();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { popularGifts } = useGiftData();

  // Pagination & Fetch States
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const lastFetchedPageRef = useRef(0);
  const isFetchingRef = useRef(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("name-asc");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const categoryFromQuery = searchParams.get("categoryId") || "";
  const appliedCategoryId =
    selectedCategory !== "All"
      ? selectedCategory
      : categoryId || categoryFromQuery;

  // Quick Add States
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);
  const [quickAddDetails, setQuickAddDetails] = useState<Product | null>(null);
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const [quickAddSelectedAttributes, setQuickAddSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [isQuickAddSubmitting, setIsQuickAddSubmitting] = useState(false);

  // Keeping your pagination hook for totalItems state
  const { totalItems, setTotalItems } = usePagination({ initialPageSize: 20 });

  // Intersection Observer ref
  const observerTarget = useRef<HTMLDivElement>(null);

  const fetchProducts = async () => {
    // Prevent duplicate API calls if we are already fetching this exact page
    if (isFetchingRef.current || lastFetchedPageRef.current === currentPage) {
      return;
    }

    try {
      isFetchingRef.current = true;
      lastFetchedPageRef.current = currentPage;

      if (currentPage === 1) setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: PRODUCTS_PER_PAGE.toString(),
      });

      // Include Description
      params.append("includeDescription", "true");
      params.append("isActive", "true");

      if (appliedCategoryId) {
        params.append("categoryId", appliedCategoryId);
      }

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      if (sortBy) {
        params.append("sortBy", sortBy);
      }

      if (priceRange.min > 0) {
        params.append("minPrice", priceRange.min.toString());
      }

      if (priceRange.max > 0) {
        params.append("maxPrice", priceRange.max.toString());
      }

      const res = await BaseApi.get(`/products?${params}`);

      if (res?.success && res?.data) {
        const fetchedProducts = res.data;
        const paginationData = res.pagination;

        if (currentPage === 1) {
          setProducts(fetchedProducts);
        } else {
          setProducts((prev) => {
            const existingIds = new Set(prev.map((p) => p.id));
            const uniqueNewProducts = fetchedProducts.filter(
              (p: Product) => !existingIds.has(p.id),
            );
            return [...prev, ...uniqueNewProducts];
          });
        }

        setTotalItems(paginationData?.totalItems || fetchedProducts.length);
        setHasMore(paginationData?.hasNextPage ?? false);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      // Reset the ref on error so the user can try loading this page again
      lastFetchedPageRef.current = currentPage - 1;
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Fetch when currentPage or selectedCategory changes
  useEffect(() => {
    fetchProducts();
  }, [
    currentPage,
    appliedCategoryId,
    searchTerm,
    sortBy,
    priceRange.min,
    priceRange.max,
  ]);

  // Update search term from URL parameters
  useEffect(() => {
    const searchFromUrl = searchParams.get("search");
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
    }
  }, [searchParams]);

  // Update selectedCategory when categoryId from route changes
  useEffect(() => {
    if (categoryId) {
      setSelectedCategory(categoryId);
    } else if (categoryFromQuery) {
      setSelectedCategory(categoryFromQuery);
    } else {
      setSelectedCategory("All");
    }
    if (!searchParams.get("search")) {
      setSearchTerm("");
    }
    setSortBy("name-asc");

    lastFetchedPageRef.current = 0; // Reset tracker for category change
    setCurrentPage(1);
  }, [categoryId, categoryFromQuery, searchParams]);

  // Reset page when filters change (safely watching specific primitives)
  useEffect(() => {
    if (currentPage !== 1) {
      lastFetchedPageRef.current = 0;
      setCurrentPage(1);
      setProducts([]); // 🔥 IMPORTANT
    }
  }, [searchTerm, selectedCategory, sortBy, priceRange.min, priceRange.max]);

  // Filter and sort logic
  const getFilteredAndSortedProducts = () => {
    let filtered = [...products];
    const hasMinPriceFilter = priceRange.min > 0;
    const hasMaxPriceFilter = priceRange.max > 0;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((product) =>
        (product?.name?.en || product?.name?.ar || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    }

    // Price range filter
    filtered = filtered.filter((product) => {
      const productPrice = Number(product.price);

      if (hasMinPriceFilter && productPrice < priceRange.min) {
        return false;
      }

      if (hasMaxPriceFilter && productPrice > priceRange.max) {
        return false;
      }

      return true;
    });

    // Sorting
    switch (sortBy) {
      case "name-asc":
        filtered.sort((a, b) =>
          (a.name.en || b.name.ar || "").localeCompare(
            b.name.en || b.name.ar || "",
          ),
        );
        break;
      case "name-desc":
        filtered.sort((a, b) =>
          (b.name.en || "").localeCompare(a.name.en || ""),
        );
        break;
      case "price-low":
        filtered.sort((a, b) => Number(a.price) - Number(b.price));
        break;
      case "price-high":
        filtered.sort((a, b) => Number(b.price) - Number(a.price));
        break;
      case "newest":
        filtered.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      default:
        break;
    }

    return filtered;
  };

  const allFilteredProducts = getFilteredAndSortedProducts();

  // Paginate the filtered products correctly using currentPage
  const paginatedProducts = allFilteredProducts;

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      // Safely increment the page only if not currently fetching
      if (
        target.isIntersecting &&
        hasMore &&
        !loading &&
        !isFetchingRef.current
      ) {
        setCurrentPage((prev) => prev + 1);
      }
    },
    [hasMore, loading],
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "300px",
      threshold: 0.1,
    });

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [handleObserver]);

  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    lastFetchedPageRef.current = 0;
    setCurrentPage(1);
    setProducts([]); // 🔥 IMPORTANT
  }, [searchTerm, selectedCategory, sortBy, priceRange.min, priceRange.max]);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory(categoryId || "All");
    setSortBy("name-asc");

    lastFetchedPageRef.current = 0;
    setCurrentPage(1);
    setProducts([]); // ✅ ADD THIS

    setLoading(false);
    setSearchParams({});
    setPriceRange({ min: 0, max: 0 });
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);

    const nextParams = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      nextParams.set("categoryId", value);
    } else {
      nextParams.delete("categoryId");
    }
    setSearchParams(nextParams);
  };

  const openQuickAddModal = async (product: Product) => {
    if (!isAuthenticated) {
      navigate("/signin", { state: { from: location } });
      return;
    }

    setIsQuickAddOpen(true);
    setQuickAddProduct(product);
    setQuickAddDetails(null);
    setQuickAddSelectedAttributes({});
    setQuickAddError(null);
    setQuickAddLoading(true);

    try {
      const res = await BaseApi.get(`/products/${product.id}?isActive=true`);
      const detail = res?.data?.product || res?.data;

      if (detail) {
        setQuickAddDetails(detail);
      } else {
        setQuickAddError(t("Failed to load product options"));
      }
    } catch (error: any) {
      console.error("Failed to load product details:", error);
      setQuickAddError(error?.message || t("Failed to load product options"));
    } finally {
      setQuickAddLoading(false);
    }
  };

  const closeQuickAddModal = () => {
    setIsQuickAddOpen(false);
    setQuickAddProduct(null);
    setQuickAddDetails(null);
    setQuickAddSelectedAttributes({});
    setQuickAddError(null);
    setIsQuickAddSubmitting(false);
  };

  const modalProduct = quickAddDetails || quickAddProduct;

  const allowedVariationTypes = useMemo(
    () =>
      (quickAddDetails?.variationTypes || [])
        .map((type) => type?.toLowerCase?.())
        .filter(Boolean),
    [quickAddDetails?.variationTypes],
  );

  const quickAddVariationOptions = useMemo(() => {
    const configuredTypes = allowedVariationTypes.filter(
      (type): type is string => typeof type === "string" && type.length > 0,
    );
    const fallbackTypes = Array.from(
      new Set(
        (quickAddDetails?.variations || []).flatMap((variation) =>
          Object.entries(variation.attributes || {})
            .filter(([, value]) => Boolean(value))
            .map(([key]) => key),
        ),
      ),
    );
    const variationTypesToUse =
      configuredTypes.length > 0 ? configuredTypes : fallbackTypes;
    const optionsMap: Record<string, Set<string>> = {};
    variationTypesToUse.forEach((type) => {
      optionsMap[type] = new Set();
    });

    (quickAddDetails?.variations || []).forEach((variation) => {
      variationTypesToUse.forEach((type) => {
        const value = variation.attributes?.[type];
        if (value) {
          optionsMap[type].add(value);
        }
      });
    });

    return Object.fromEntries(
      Object.entries(optionsMap)
        .map(([key, set]) => [key, Array.from(set)] as const)
        .filter(([, values]) => values.length > 0),
    );
  }, [allowedVariationTypes, quickAddDetails?.variations]);

  const variationKeys = Object.keys(quickAddVariationOptions);
  const requiresVariationSelection =
    (quickAddDetails?.variations || []).length > 0 && variationKeys.length > 0;

  const selectedVariation = useMemo(() => {
    if (!requiresVariationSelection) return null;
    if (!variationKeys.every((key) => quickAddSelectedAttributes[key])) {
      return null;
    }

    return (quickAddDetails?.variations || []).find((variation) =>
      variationKeys.every(
        (key) =>
          variation.attributes?.[key] === quickAddSelectedAttributes[key],
      ),
    );
  }, [
    requiresVariationSelection,
    variationKeys,
    quickAddSelectedAttributes,
    quickAddDetails?.variations,
  ]);

  const isModalOutOfStock = requiresVariationSelection
    ? !!selectedVariation && selectedVariation.stock <= 0
    : (modalProduct?.stock ?? 0) <= 0;

  const handleQuickAddAttributeClick = (type: string, value: string) => {
    setQuickAddSelectedAttributes((prev) => ({ ...prev, [type]: value }));
  };

  const handleQuickAddToCart = async () => {
    if (!modalProduct) return;

    if (quickAddLoading || quickAddError) {
      return;
    }

    if (!isAuthenticated) {
      navigate("/signin", { state: { from: location } });
      return;
    }

    if (requiresVariationSelection && !selectedVariation) {
      err(t("Please select options"));
      return;
    }

    if (isModalOutOfStock) {
      err(t("Out of Stock"));
      return;
    }

    setIsQuickAddSubmitting(true);

    try {
      const payload: Record<string, any> = {
        productId: modalProduct.id,
        quantity: 1,
      };

      if (selectedVariation?.id) {
        payload.variationId = selectedVariation.id;
      }

      variationKeys.forEach((key) => {
        const value = quickAddSelectedAttributes[key];
        if (value) {
          payload[key] = value;
        }
      });

      const res = await BaseApi.post("/cart/items", payload);
      if (!res?.success) {
        throw new Error(res?.message || "Failed to add to cart");
      }

      success(t("Product added to cart successfully!"));
      window.dispatchEvent(
        new CustomEvent("cartUpdated", {
          detail: { action: "add", productId: modalProduct.id },
        }),
      );
      closeQuickAddModal();
    } catch (error: any) {
      console.error("Failed to add to cart:", error);
      err(error?.message || t("Failed to add to cart. Please try again."));
    } finally {
      setIsQuickAddSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f2e6db] p-4 sm:py-4 lg:px-14">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideRight { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6 overflow-hidden"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-gray-600 to-black p-3 rounded-xl shadow-lg">
                <Package className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {t("Discover Our Collection")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {`${totalItems} ${t("products available")}`}
                  {searchTerm && (
                    <span className="ml-2 text-amber-600">
                      {t("for")} "{searchTerm}"
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="products-header">
        <button
          className="toggle-filters-btn"
          style={{ backgroundColor: "black" }}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={18} />
        </button>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="filters-container">
          {/* Search */}
          <div className="filter-group">
            <label>{t("Search")}</label>
            <input
              type="text"
              placeholder={t("Search products...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Sort By */}
          <div className="filter-group">
            <label>{t("Sort By")}</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name-asc">{t("Name (A-Z)")}</option>
              <option value="name-desc">{t("Name (Z-A)")}</option>
              <option value="price-low">{t("Price (Low to High)")}</option>
              <option value="price-high">{t("Price (High to Low)")}</option>
              <option value="newest">{t("Newest First")}</option>
              <option value="oldest">{t("Oldest First")}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="filter-group">
            <label>{t("Category")}</label>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="filter-select"
            >
              <option value="All">{t("All Categories")}</option>
              {(categories || []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name.en}
                </option>
              ))}
            </select>
          </div>

          {/* Price Range */}
          <div className="filter-group">
            <label>{t("Price Range")}</label>
            <div className="price-range-inputs">
              <input
                type="number"
                placeholder={t("Min")}
                value={priceRange.min}
                onChange={(e) =>
                  setPriceRange({
                    ...priceRange,
                    min: Number(e.target.value) || 0,
                  })
                }
                className="filter-input price-input"
              />
              <span>-</span>
              <input
                type="number"
                placeholder={t("Max")}
                value={priceRange.max}
                onChange={(e) =>
                  setPriceRange({
                    ...priceRange,
                    max: Number(e.target.value) || 0,
                  })
                }
                className="filter-input price-input"
              />
            </div>
          </div>

          {/* Reset Button */}
          <button onClick={resetFilters} className="reset-filters-btn">
            {t("Reset Filters")}
          </button>
        </div>
      )}

      {/* Products Grid */}
      {loading ? (
        <LoadingComponent title={t("Products")} />
      ) : allFilteredProducts.length === 0 ? (
        <div className="no-products">
          <div className="p-8 sm:p-16 text-center">
            <Package className="w-12 h-12 sm:w-16 sm:h-16 text-gray-800 mx-auto mb-4" />
            <p className="text-gray-500 text-base sm:text-lg">
              {searchTerm
                ? t(
                    'No products found for "{{searchTerm}}". Please try different keywords.',
                    { searchTerm },
                  )
                : t("No products found. Please check your filters.")}
            </p>
          </div>
          <button onClick={resetFilters} className="reset-btn">
            {t("Clear Filters")}
          </button>
        </div>
      ) : (
        <>
          <div className="products-grid">
            {paginatedProducts.map((product: Product) => (
              <div
                key={product.id}
                style={{
                  // padding: "0 0.5rem",
                  boxSizing: "border-box",
                  opacity: 0,
                  animation: `slideIn 0.6s ease-out forwards`,
                }}
              >
                <ProductCard
                  key={product.id}
                  product={product}
                  canAddToCart={true}
                  canAddToFavorite={true}
                  canEdit={canEditProduct}
                  canRate={!canCreateAddProduct}
                  showRating={!!product.ratingCount ? true : false}
                  isShadow={true}
                  isPopularGift={popularGifts.includes(product.id)}
                  onAddToCartClick={openQuickAddModal}
                />
              </div>
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div
              ref={observerTarget}
              className="loading-container"
              style={{ padding: "20px", textAlign: "center" }}
            >
              <p>{t("Loading more products...")}</p>
            </div>
          )}

          {/* End of Results Message */}
          {!hasMore && paginatedProducts.length > 0 && (
            <div
              style={{ padding: "20px", textAlign: "center", color: "#666" }}
            >
              <p>{t("You've reached the end of the list")}</p>
            </div>
          )}
        </>
      )}

      {isQuickAddOpen && (
        <div className="quick-add-backdrop" onClick={closeQuickAddModal}>
          <div className="quick-add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quick-add-header">
              <div>
                <h3 className="quick-add-title">
                  {(i18n.language === "ar"
                    ? modalProduct?.name?.ar
                    : modalProduct?.name?.en) || t("Product")}
                </h3>
                {modalProduct && (
                  <p className="quick-add-subtitle">
                    <Price amount={Number(modalProduct.price)} />
                  </p>
                )}
              </div>
              <button
                type="button"
                className="quick-add-close"
                onClick={closeQuickAddModal}
                aria-label={t("Close")}
              >
                ×
              </button>
            </div>

            <div className="quick-add-body">
              {quickAddLoading ? (
                <div className="quick-add-loading">
                  <span className="quick-add-spinner" />
                  <span>{t("Loading options...")}</span>
                </div>
              ) : quickAddError ? (
                <div className="quick-add-error">{quickAddError}</div>
              ) : requiresVariationSelection ? (
                <div className="quick-add-options">
                  {Object.entries(quickAddVariationOptions).map(
                    ([type, values]) => (
                      <div key={type} className="quick-add-option-group">
                        <p className="quick-add-option-title">
                          {t("SELECT")} {type.toUpperCase()}
                        </p>
                        <div className="quick-add-option-values">
                          {values.map((value) => (
                            <button
                              key={`${type}-${value}`}
                              type="button"
                              className={`quick-add-option-btn ${
                                quickAddSelectedAttributes[type] === value
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleQuickAddAttributeClick(type, value)
                              }
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="quick-add-note">
                  {t("No options required. You can add this item to cart.")}
                </div>
              )}
            </div>

            <div className="quick-add-footer">
              <button
                type="button"
                className="quick-add-secondary"
                onClick={closeQuickAddModal}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="add-to-cart-button quick-add-primary"
                onClick={handleQuickAddToCart}
                disabled={
                  quickAddLoading ||
                  isQuickAddSubmitting ||
                  !!quickAddError ||
                  isModalOutOfStock ||
                  (requiresVariationSelection && !selectedVariation)
                }
              >
                <span className="button-content">
                  {isQuickAddSubmitting ? (
                    <>
                      <span className="loader"></span>
                      <span className="button-text">
                        {t("Adding to Cart")}...
                      </span>
                    </>
                  ) : isModalOutOfStock ? (
                    <span className="button-text">{t("Out of Stock")}</span>
                  ) : (
                    <span className="button-text">{t("Add to Cart")}</span>
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductList;
